import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

const DEFAULT_CONFIG = {
  fecha_corte_preescolar: "2025-09-01",
  fecha_corte_primaria:   "2025-09-01",
  fecha_corte_secundaria: "2025-09-01",
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configGlobal, setConfigGlobal] = useState(DEFAULT_CONFIG);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("sep911_user", JSON.stringify(data));
      return data;
    } catch {
      return null;
    }
  }, []);

  const refreshConfigGlobal = useCallback(async () => {
    try {
      const { data } = await api.get("/config/global");
      setConfigGlobal({ ...DEFAULT_CONFIG, ...data });
      return data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("sep911_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    Promise.all([
      api.get("/auth/me").then((r) => setUser(r.data)),
      api.get("/config/global").then((r) => setConfigGlobal({ ...DEFAULT_CONFIG, ...r.data })).catch(() => {}),
    ])
      .catch(() => {
        localStorage.removeItem("sep911_token");
        localStorage.removeItem("sep911_user");
        setUser(false);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("sep911_token", data.token);
    localStorage.setItem("sep911_user", JSON.stringify(data.user));
    setUser(data.user);
    try {
      const cfg = await api.get("/config/global");
      setConfigGlobal({ ...DEFAULT_CONFIG, ...cfg.data });
    } catch { /* ignore */ }
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_e) { /* ignore */ }
    localStorage.removeItem("sep911_token");
    localStorage.removeItem("sep911_user");
    setUser(false);
    window.location.href = "/login";
  };

  const fechaCorteEdad = useCallback((nivel) => {
    const n = (nivel || "").toUpperCase();
    if (n === "PREESCOLAR") return configGlobal.fecha_corte_preescolar;
    if (n === "PRIMARIA")   return configGlobal.fecha_corte_primaria;
    if (n === "SECUNDARIA") return configGlobal.fecha_corte_secundaria;
    return configGlobal.fecha_corte_primaria; // fallback razonable
  }, [configGlobal]);

  return (
    <AuthContext.Provider
      value={{
        user, loading, login, logout, refreshUser,
        configGlobal, refreshConfigGlobal, fechaCorteEdad,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
